import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../services/api';

export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/login', credentials);
      if (response.data.twoFactorRequired) {
        return response.data;
      }
      // Prefer new accessToken; fall back to legacy token field
      const token = response.data.accessToken || response.data.token;
      localStorage.setItem('token', token);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Login failed');
    }
  }
);

export const verify2FALogin = createAsyncThunk(
  'auth/verify2FALogin',
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/verify-2fa-login', data);
      const token = response.data.accessToken || response.data.token;
      localStorage.setItem('token', token);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Verification failed');
    }
  }
);

export const registerUser = createAsyncThunk(
  'auth/registerUser',
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/register', data);
      const token = response.data.accessToken || response.data.token;
      localStorage.setItem('token', token);
      return response.data;
    } catch (error) {
      // Validation failures (for example a weak password) arrive as { errors: [{ msg }] }.
      return rejectWithValue(error.response?.data?.error || error.response?.data?.errors?.[0]?.msg || 'Registration failed');
    }
  }
);

export const checkAuth = createAsyncThunk(
  'auth/checkAuth',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/auth/profile');
      return response.data.company;
    } catch (error) {
      // Only invalidate the token when the server explicitly rejects it (401).
      // Network errors or 502s are transient — preserving the token lets the
      // user stay logged in once the backend comes back up.
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
      }
      return rejectWithValue('Authentication failed');
    }
  }
);

export const updateProfile = createAsyncThunk(
  'auth/updateProfile',
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.put('/auth/profile', data);
      return response.data.company;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Update failed');
    }
  }
);

export const updateSignature = createAsyncThunk(
  'auth/updateSignature',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await api.put('/auth/profile/signature', formData);
      return { digitalSignatureUrl: response.data.digitalSignatureUrl };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Signature upload failed');
    }
  }
);

export const updateSeal = createAsyncThunk(
  'auth/updateSeal',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await api.put('/auth/profile/seal', formData);
      return { companySealUrl: response.data.companySealUrl };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Seal upload failed');
    }
  }
);

export const updatePassword = createAsyncThunk(
  'auth/updatePassword',
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.put('/auth/profile/password', data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Password update failed');
    }
  }
);

export const updateSettings = createAsyncThunk(
  'auth/updateSettings',
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.put('/auth/profile/settings', data);
      return response.data.company;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Settings update failed');
    }
  }
);

export const generateApiKey = createAsyncThunk(
  'auth/generateApiKey',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/profile/apikey');
      return response.data.company;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to generate API key');
    }
  }
);

export const deleteAccount = createAsyncThunk(
  'auth/deleteAccount',
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/delete-account', data);
      localStorage.removeItem('token');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Account deletion failed');
    }
  }
);

export const setup2FA = createAsyncThunk(
  'auth/setup2FA',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/setup-2fa');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || '2FA setup failed');
    }
  }
);

export const verify2FA = createAsyncThunk(
  'auth/verify2FA',
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/verify-2fa', data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || '2FA verification failed');
    }
  }
);

export const disable2FA = createAsyncThunk(
  'auth/disable2FA',
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/disable-2fa', data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to disable 2FA');
    }
  }
);

export const refreshToken = createAsyncThunk(
  'auth/refreshToken',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/refresh');
      const token = response.data.accessToken || response.data.token;
      if (token) localStorage.setItem('token', token);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Token refresh failed');
    }
  }
);

// A logged-in identity can be the primary Company account (role 'admin') OR
// an invited team member (User model, role 'tenant_admin') — both are
// full-access admins, so isAdmin must recognize either, not just the literal
// string 'admin'.
const ADMIN_ROLES = ['admin', 'tenant_admin', 'superadmin'];

const initialState = {
  isAuthenticated: false,
  user: null,
  isAdmin: false,
  isSuperAdmin: false,
  twoFactorRequired: false,
  tempToken: null,
  loading: false,
  authChecking: false,
  error: null,
  success: false
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.isAdmin = false;
      state.isSuperAdmin = false;
      state.error = null;
      localStorage.removeItem('token');
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.twoFactorRequired) {
          state.twoFactorRequired = true;
          state.tempToken = action.payload.tempToken;
        } else {
          state.isAuthenticated = true;
          state.user = action.payload.company;
          state.isAdmin = ADMIN_ROLES.includes(action.payload.company.role);
          state.isSuperAdmin = action.payload.company.role === 'superadmin';
          state.twoFactorRequired = false;
          state.tempToken = null;
        }
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Verify 2FA Login
      .addCase(verify2FALogin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verify2FALogin.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.company;
        state.isAdmin = ADMIN_ROLES.includes(action.payload.company.role);
        state.isSuperAdmin = action.payload.company.role === 'superadmin';
        state.twoFactorRequired = false;
        state.tempToken = null;
      })
      .addCase(verify2FALogin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Register
      .addCase(registerUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.company;
        state.isAdmin = ADMIN_ROLES.includes(action.payload.company.role);
        state.isSuperAdmin = action.payload.company.role === 'superadmin';
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Check Auth
      .addCase(checkAuth.pending, (state) => {
        state.authChecking = true;
      })
      .addCase(checkAuth.fulfilled, (state, action) => {
        state.authChecking = false;
        state.isAuthenticated = true;
        state.user = action.payload;
        state.isAdmin = ADMIN_ROLES.includes(action.payload?.role);
        state.isSuperAdmin = action.payload?.role === 'superadmin';
      })
      .addCase(checkAuth.rejected, (state) => {
        state.authChecking = false;
        // Only logout if not already authenticated (preserve session on transient errors)
        if (!state.isAuthenticated) {
          state.user = null;
          state.isAdmin = false;
          state.isSuperAdmin = false;
        }
      })
      .addCase(deleteAccount.fulfilled, (state) => {
        state.isAuthenticated = false;
        state.user = null;
        state.isAdmin = false;
        state.isSuperAdmin = false;
        state.loading = false;
      })
      .addCase(updatePassword.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(updateSignature.fulfilled, (state, action) => {
        if (state.user) state.user.digitalSignatureUrl = action.payload.digitalSignatureUrl;
      })
      .addCase(updateSeal.fulfilled, (state, action) => {
        if (state.user) state.user.companySealUrl = action.payload.companySealUrl;
      })
      // Update Profile / Settings / API Key
      .addMatcher(
        (action) => [
          updateProfile.pending, updateSettings.pending, generateApiKey.pending, updatePassword.pending
        ].includes(action.type),
        (state) => { state.loading = true; state.error = null; state.success = false; }
      )
      .addMatcher(
        (action) => [
          updateProfile.fulfilled, updateSettings.fulfilled, generateApiKey.fulfilled
        ].includes(action.type),
        (state, action) => {
          state.loading = false;
          state.user = action.payload;
          state.success = true;
        }
      )
      .addMatcher(
        (action) => [
          updateProfile.rejected, updateSettings.rejected, generateApiKey.rejected, updatePassword.rejected
        ].includes(action.type),
        (state, action) => {
          state.loading = false;
          state.error = action.payload;
          state.success = false;
        }
      );
  },
});

export const { logout, clearError } = authSlice.actions;
export default authSlice.reducer;
